import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, Snackbar, Alert, Container } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { ProductsProvider } from './contexts/ProductsContext';
import { ProductProvider } from './contexts/ProductContext';
import { AuthProvider } from './contexts/AuthContext';
import { ListProvider } from './contexts/ListContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AdminPanel from './components/AdminPanel';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import SiteFooter from './components/SiteFooter';
import ProtectedRoute from './components/ProtectedRoute';
import FavoritesPage from './pages/FavoritesPage';
import ProfilePage from './pages/ProfilePage';
import ProductPage from './pages/ProductPage';
import ListPage from './pages/ListPage';
import { ProductList } from './components/ProductList';
import FirebaseTest from './components/FirebaseTest';
import FirestoreTest from './components/FirestoreTest';
import type { ProductCategory } from './constants/categories';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

const theme = createTheme({
  shape: { borderRadius: 0 },
  palette: {
    primary: { main: '#111111' },
    secondary: { main: '#10b981' },
    background: { default: '#f9fafb' },
    text: { primary: '#2006FE' },
  },
  typography: {
    fontFamily: '"Helvetica Neue", sans-serif',
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    // Global input border color overrides
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--blue)',
            borderRadius: 0,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--blue)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--blue)',
            borderWidth: 2,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottomColor: 'var(--blue)',
          },
          '&:hover:before': {
            borderBottomColor: 'var(--blue)',
          },
          '&:after': {
            borderBottomColor: 'var(--blue)',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottomColor: 'var(--blue)',
          },
          '&:hover:before': {
            borderBottomColor: 'var(--blue)',
          },
          '&:after': {
            borderBottomColor: 'var(--blue)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--blue)',
          '&.Mui-focused': {
            color: 'var(--blue)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--blue)',
          '&.Mui-focused': {
            color: 'var(--blue)',
          },
        },
      },
    },
  },
});

const NotFoundPage = () => (
  <Box sx={{ pt: 8, pb: 4, textAlign: 'center' }}>
    <h1>404 - Page Not Found</h1>
  </Box>
);

function App() {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Category filter state lifted to the app so Navbar controls it
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | ''>('');
  // Search query lifted to the app and controlled by the Navbar
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ProductsProvider>
          <ProductProvider>
            <ListProvider>
              <Router>
                <ScrollToTop />
                <Analytics />
                <Navbar 
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                  searchQuery={searchQuery}
                  onSearch={setSearchQuery}
                />
                <Box component="main" className="main-content" sx={{ pt: 8 }}>
                  <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Routes>
                      <Route path="/login" element={
                        <LoginPage onLoginSuccess={() => {
                          setSnackbar({
                            open: true,
                            message: 'Login successful!',
                            severity: 'success',
                          });
                        }} />
                      } />
                      <Route path="/register" element={
                        <RegisterPage onRegisterSuccess={() => {
                          setSnackbar({
                            open: true,
                            message: 'Account created successfully! Welcome to your favorites.',
                            severity: 'success',
                          });
                        }} />
                      } />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/admin" element={
                        <ProtectedRoute adminOnly>
                          <AdminPanel />
                        </ProtectedRoute>
                      } />
                      <Route path="/products/:id" element={<ProductPage />} />
                      <Route path="/favorites" element={
                        <ProtectedRoute>
                          <FavoritesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/list/:listId" element={
                        <ProtectedRoute>
                          <ListPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/" element={
                        <ProductList selectedCategory={selectedCategory} searchQuery={searchQuery} />
                      } />
                      <Route path="*" element={<NotFoundPage />} />
                      {/* Temporary route for testing Firebase connection */}
                      <Route path="/firebase-test" element={<FirebaseTest />} />
              <Route path="/firestore-test" element={<FirestoreTest />} />
                    </Routes>
                  </Container>
                </Box>
                <SiteFooter />
              </Router>
              <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
              >
                <Alert 
                  onClose={handleCloseSnackbar} 
                  severity={snackbar.severity}
                  sx={{ width: '100%' }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>
            </ListProvider>
          </ProductProvider>
        </ProductsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
